"use client";

import { Carousel, CarouselContent, CarouselItem, cn, type CarouselApi } from "@saridana-core/react-ui-kit";
import { IImage, TOurHistorySection, TOurHistorySectionDate } from "@shared/types";
import { HtmlParser, SectionHeading } from "@shared/ui";
import Image from "next/image";

import { useEffect, useState } from "react";

function TimelineCarousel({
  items,
  secondsToChange = 5,
  ornament,
}: {
  items: TOurHistorySectionDate[];
  secondsToChange?: number;
  ornament?: IImage;
}) {
  const [api, setApi] = useState<CarouselApi | null>(null);
  const [current, setCurrent] = useState(1);
  const [progressKey, setProgressKey] = useState(0);

  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setCurrent(api.selectedScrollSnap() + 1);
      setProgressKey((k) => k + 1);
    };

    onSelect();
    api.on("select", onSelect);

    return () => {
      api.off("select", onSelect);
    };
  }, [api]);

  const delay = secondsToChange * 1000;

  return (
    <div
      style={{ background: "linear-gradient(260.62deg, #0C3245 14.18%, #12A9D8 81.82%)" }}
      className="relative overflow-hidden lg:rounded-2xl -mx-4 lg:mx-0"
    >
      {ornament?.data?.attributes?.url && (
        <Image
          src={ornament?.data?.attributes?.url || ""}
          alt={ornament?.data?.attributes?.alternativeText || ornament?.data?.attributes?.name || ""}
          width={ornament?.data?.attributes?.width || 360}
          height={ornament?.data?.attributes?.height || 360}
          className={cn("absolute top-0 left-0 w-[220px] sm:w-[360px] h-auto")}
        />
      )}

      <div className="relative overflow-hidden pt-[60px] sm:pt-20 lg:pt-[100px] pb-10">
        <div className="lg:fade-right-primary pointer-events-none absolute right-0 top-0 h-full w-10 md:w-16 lg:w-24 z-10" />
        <div className="flex flex-col gap-6 w-full">
          <Carousel
            setApi={setApi}
            opts={{
              align: "start",
              loop: false,
            }}
            plugins={[{ plugin: "autoplay", options: { active: !!secondsToChange, delay } }]}
            className="w-full"
          >
            <CarouselContent>
              {items.map((item, index) => (
                <CarouselItem
                  key={"our-history-date-" + item.id}
                  className="basis-[100%] sm:basis-[80%] xl:basis-[65%] px-9 sm:px-14 lg:px-24 pb-20"
                >
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="relative flex size-9 sm:size-10 rounded-xl overflow-hidden">
                      <div
                        className={cn(
                          "absolute z-1 top-0 right-0 bg-white h-full w-full duration-200 transform-all",
                          index < current ? "opacity-100" : "opacity-20",
                        )}
                      ></div>
                      {item?.icon?.data?.attributes?.url && (
                        <Image
                          src={item.icon?.data?.attributes?.url || ""}
                          alt={item.icon?.data?.attributes?.alternativeText || item.icon?.data?.attributes?.name || ""}
                          width={item.icon?.data?.attributes?.width || 24}
                          height={item.icon?.data?.attributes?.height || 24}
                          className={cn(
                            "p-1.5 sm:p-2 w-full h-full relative duration-200 transform-all ",
                            index < current ? "grayscale-0" : "grayscale brightness-150",
                          )}
                        />
                      )}
                    </div>
                    <h4 className="text-white font-semibold text-4xl sm:text-5xl leading-[115%]">{item?.date}</h4>
                  </div>

                  <div className="relative flex items-center mb-10 -left-[calc(100%-27px)]">
                    <div
                      className={cn(
                        "flex-1 h-[2px] w-full -ml-[5rem]  sm:-ml-[8rem] lg:-ml-[12rem] bg-white",
                        index < current ? "opacity-100" : "opacity-20",
                      )}
                    ></div>
                    <div
                      className={cn(
                        "flex-shrink-0 relative z-2 size-4 border-2 rounded-full",
                        index < current ? "bg-primary-500 border-white" : "bg-primary-500 border-primary-500",
                      )}
                    ></div>
                  </div>

                  <h3 className="text-white font-bold text-4xl sm:text-5xl leading-[115%] mb-4">{item.title}</h3>
                  {item.description && (
                    <div className="text-white text-base sm:text-lg leading-[146%]">
                      <HtmlParser data={item.description} />
                    </div>
                  )}
                </CarouselItem>
              ))}
            </CarouselContent>
          </Carousel>
        </div>
      </div>

      <div className="absolute z-1 right-0 top-0 h-full">
        <div
          style={{ background: "linear-gradient(270deg, rgba(12, 50, 69, 0.4) 0%, rgba(12, 50, 69, 0) 100%)" }}
          className="sm:hidden h-full w-20"
        ></div>
        <div
          style={{ background: "linear-gradient(270deg, #0C3245 0%, rgba(12, 50, 69, 0) 100%)" }}
          className="hidden sm:block h-full w-[240px] lg:w-[560px]"
        ></div>
      </div>

      <div className="relative z-2 flex justify-center gap-2 sm:gap-4 w-full px-6 sm:px-10 pb-10">
        {items.map((_, i) => {
          const isActive = current === i + 1;
          const isPassed = current > i + 1;

          return (
            <div
              key={i}
              onClick={() => api?.scrollTo(i)}
              className="relative h-2 sm:h-3 w-full cursor-pointer overflow-hidden rounded-full"
              style={{
                background: "rgba(255, 255, 255, 0.1)",
              }}
            >
              <div
                key={isActive ? progressKey : `static-${i}`}
                className="absolute left-0 top-0 h-full rounded-full bg-white"
                style={{
                  width: isPassed ? "100%" : "0%",
                  animation: isActive ? `story-progress ${delay}ms linear forwards` : "none",
                }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function OurHistorySection({ title, dates, secondsToChange, ornament }: TOurHistorySection) {
  return (
    <section className="py-24 md:py-[120px] relative overflow-hidden bg-white">
      <div className="container relative">
        <SectionHeading className="text-center" title={title} titleClassName="max-w-3xl" />

        <div className="mt-14 md:mt-[70px]">
          <TimelineCarousel secondsToChange={secondsToChange} items={dates} ornament={ornament} />
        </div>
      </div>
    </section>
  );
}
